import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { datingAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Progress } from '../components/ui/progress';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
  Heart, X, MessageCircle, MapPin, GraduationCap, Loader2,
  ChevronDown, ChevronUp, Flag, Users, TrendingUp, ArrowRight, Eye, EyeOff, ShieldCheck
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import usePageTitle from '../hooks/usePageTitle';

const Dating = () => {
  usePageTitle('Dating');
  const { user, updateProfile } = useAuth();
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [matches, setMatches] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('browse');
  const [showFullProfile, setShowFullProfile] = useState(false);
  const [swipeDirection, setSwipeDirection] = useState(null);
  const [interestedIn, setInterestedIn] = useState('everyone');
  // Default OFF — members must opt in to be shown in the dating feed.
  const [datingVisible, setDatingVisible] = useState(false);
  const [savingVisibility, setSavingVisibility] = useState(false);

  // Hydrate the toggle from the user record once loaded (or after login).
  useEffect(() => {
    if (user) setDatingVisible(user.dating_opted_in === true);
  }, [user?.user_id, user?.dating_opted_in]);

  const toggleDatingVisible = async (next) => {
    setDatingVisible(next);
    setSavingVisibility(true);
    try {
      await updateProfile({ dating_opted_in: next });
      toast.success(next ? 'You are now discoverable in Dating.' : 'You are hidden from Dating.');
    } catch {
      setDatingVisible(!next);
      toast.error('Failed to update dating visibility');
    } finally {
      setSavingVisibility(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [profilesRes, matchesRes] = await Promise.allSettled([
          datingAPI.getProfiles({ limit: 20 }),
          datingAPI.getMatches()
        ]);
        if (profilesRes.status === 'fulfilled') setProfiles(Array.isArray(profilesRes.value.data) ? profilesRes.value.data : []);
        if (matchesRes.status === 'fulfilled') setMatches(Array.isArray(matchesRes.value.data) ? matchesRes.value.data : []);
      } catch (error) {
        console.error('Failed to fetch dating data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const currentProfile = profiles[currentIndex];

  const handleLike = async () => {
    if (!currentProfile) return;
    setSwipeDirection('right');
    try {
      const res = await datingAPI.like(currentProfile.profile_id);
      if (res.data?.matched) {
        toast.success(`You matched with ${currentProfile.name}!`, { duration: 4000 });
        setMatches(prev => [...prev, currentProfile]);
      }
    } catch (error) {
      toast.error('Failed to like profile');
    }
    setTimeout(() => {
      setSwipeDirection(null);
      setCurrentIndex(prev => prev + 1);
      setShowFullProfile(false);
    }, 300);
  };

  const handlePass = () => {
    setSwipeDirection('left');
    setTimeout(() => {
      setSwipeDirection(null);
      setCurrentIndex(prev => prev + 1);
      setShowFullProfile(false);
    }, 300);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-slate-gray" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-lg mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="font-heading font-extrabold text-3xl text-dark-slate">Dating</h1>
          <p className="text-slate-gray mt-1">Meet fellow HBCU alumni</p>
        </div>

        {/* Safety callout — visible to everyone, every visit */}
        <div className="bg-rose-50/60 border border-rose-100 rounded-2xl p-4 mb-4 flex items-start gap-3">
          <ShieldCheck className="w-5 h-5 text-rose-500 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-rose-900/80 leading-relaxed">
            <p className="font-semibold text-rose-900 mb-1">A note on safety</p>
            <p>
              We do everything we can to screen for abusive behavior, but ultimately the choice
              of who to connect with is yours. If something feels off — or someone crosses a line —
              please{' '}
              <button
                type="button"
                onClick={() => navigate('/notifications?tab=reports')}
                className="font-semibold text-rose-700 underline hover:text-rose-800"
              >
                report it
              </button>{' '}
              right away. You can also tap the flag icon on any profile to report directly.
            </p>
          </div>
        </div>

        {/* Dating visibility toggle — defaults to OFF; explicit opt-in required */}
        <div className="bg-white rounded-2xl p-4 border border-slate-100 shadow-ambient mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {datingVisible ? (
                <Eye className="w-5 h-5 text-rose-500" />
              ) : (
                <EyeOff className="w-5 h-5 text-slate-400" />
              )}
              <div>
                <p className="text-sm font-semibold text-dark-slate">Discoverable in Dating</p>
                <p className="text-xs text-slate-gray">
                  {datingVisible
                    ? 'Other members can see your profile in their dating feed.'
                    : "You're hidden by default. Toggle on to be shown to other members."}
                </p>
              </div>
            </div>
            <Switch
              checked={datingVisible}
              disabled={savingVisibility}
              onCheckedChange={toggleDatingVisible}
              className="data-[state=checked]:bg-rose-500"
            />
          </div>
        </div>

        {/* Interested In filter */}
        <div className="bg-white rounded-2xl p-4 border border-slate-100 shadow-ambient mb-6">
          <p className="text-xs font-semibold text-slate-gray uppercase tracking-wide mb-3">Interested In</p>
          <div className="flex rounded-full bg-slate-100 p-1">
            {[
              { value: 'women', label: 'Women' },
              { value: 'men', label: 'Men' },
              { value: 'everyone', label: 'Everyone' },
            ].map(option => (
              <button
                key={option.value}
                onClick={() => setInterestedIn(option.value)}
                className={`flex-1 py-2 rounded-full text-sm font-medium transition-colors ${
                  interestedIn === option.value
                    ? 'bg-rose-500 text-white shadow-sm'
                    : 'text-slate-gray hover:text-dark-slate'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* Profile completion banner */}
        {user?.profile_completion < 60 && (
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl p-5 border border-amber-100 mb-6">
            <div className="flex items-start space-x-3">
              <TrendingUp className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-amber-800 mb-1">Complete your profile for better matches</p>
                <p className="text-xs text-amber-700 mb-3">
                  Profiles with more details get up to 3x more matches. Add your bio, interests, and photos!
                </p>
                <div className="mb-3">
                  <div className="flex justify-between text-xs text-amber-700 mb-1">
                    <span>Profile completion</span>
                    <span>{user.profile_completion ?? 0}%</span>
                  </div>
                  <Progress value={user.profile_completion ?? 0} className="h-2 bg-amber-100 [&>div]:bg-amber-500" />
                </div>
                <Link to="/profile">
                  <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-white rounded-full text-xs px-4">
                    Complete Profile <ArrowRight className="w-3 h-3 ml-1" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex bg-slate-100 rounded-full p-1 mb-6">
          <button onClick={() => setActiveTab('browse')}
            className={`flex-1 py-2.5 rounded-full text-sm font-medium transition-colors ${
              activeTab === 'browse' ? 'bg-white text-dark-slate shadow-sm' : 'text-slate-gray'
            }`}>
            <Heart className="w-4 h-4 inline mr-1.5" />Browse
          </button>
          <button onClick={() => setActiveTab('matches')}
            className={`flex-1 py-2.5 rounded-full text-sm font-medium transition-colors ${
              activeTab === 'matches' ? 'bg-white text-dark-slate shadow-sm' : 'text-slate-gray'
            }`}>
            <Users className="w-4 h-4 inline mr-1.5" />Matches ({matches.length})
          </button>
        </div>

        {activeTab === 'browse' ? (
          <>
            {!currentProfile ? (
              <div className="bg-white rounded-3xl p-12 text-center border border-slate-100 shadow-ambient">
                <Heart className="w-16 h-16 text-slate-200 mx-auto mb-4" />
                <h3 className="font-heading font-bold text-xl text-dark-slate mb-2">No more profiles</h3>
                <p className="text-slate-gray mb-4">Check back later for new people!</p>
                <Button variant="outline" onClick={() => setActiveTab('matches')} className="rounded-full">
                  View Your Matches
                </Button>
              </div>
            ) : (
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentProfile.profile_id}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{
                    opacity: 1, scale: 1,
                    x: swipeDirection === 'right' ? 300 : swipeDirection === 'left' ? -300 : 0,
                    rotate: swipeDirection === 'right' ? 15 : swipeDirection === 'left' ? -15 : 0,
                  }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="bg-white rounded-3xl border border-slate-100 shadow-ambient overflow-hidden"
                >
                  {/* Profile Card */}
                  <div className="relative">
                    {currentProfile.photos?.[0] ? (
                      <img src={currentProfile.photos[0]} alt={currentProfile.name}
                        className="w-full h-[400px] object-cover" />
                    ) : (
                      <div className="w-full h-[400px] bg-gradient-to-br from-rose-100 to-pink-200 flex items-center justify-center">
                        <Avatar className="w-32 h-32">
                          <AvatarFallback className="bg-rose-300 text-white text-5xl">{currentProfile.name?.charAt(0)}</AvatarFallback>
                        </Avatar>
                      </div>
                    )}

                    {/* Gradient overlay */}
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent p-6">
                      <div className="flex items-end justify-between">
                        <div>
                          <h2 className="text-white font-heading font-bold text-2xl">
                            {currentProfile.name}{currentProfile.age ? `, ${currentProfile.age}` : ''}
                          </h2>
                          {currentProfile.location && (
                            <p className="text-white/80 flex items-center mt-1">
                              <MapPin className="w-4 h-4 mr-1" />
                              {currentProfile.location}
                              {currentProfile.distance_miles != null && (
                                <span className="ml-1.5 text-white/70">· {Math.round(currentProfile.distance_miles)} mi away</span>
                              )}
                            </p>
                          )}
                          {!currentProfile.location && currentProfile.distance_miles != null && (
                            <p className="text-white/80 flex items-center mt-1">
                              <MapPin className="w-4 h-4 mr-1" />
                              {Math.round(currentProfile.distance_miles)} mi away
                            </p>
                          )}
                        </div>
                        {currentProfile.compatibility_score && (
                          <Badge className="bg-rose-500 text-white text-sm px-3 py-1">
                            {currentProfile.compatibility_score}% Match
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Profile details */}
                  <div className="p-5">
                    {currentProfile.institution && (
                      <p className="text-sm text-slate-gray flex items-center mb-3">
                        <GraduationCap className="w-4 h-4 mr-1.5" />{currentProfile.institution}
                      </p>
                    )}

                    {currentProfile.interested_in && (
                      <div className="flex items-center mb-3">
                        <Heart className="w-3.5 h-3.5 mr-1.5 text-rose-400" />
                        <span className="text-xs text-slate-gray">
                          Interested in{' '}
                          <span className="font-medium text-dark-slate">
                            {currentProfile.interested_in === 'everyone'
                              ? 'Everyone'
                              : currentProfile.interested_in === 'men'
                              ? 'Men'
                              : 'Women'}
                          </span>
                        </span>
                      </div>
                    )}

                    {currentProfile.hobbies?.length > 0 && (
                      <div className={`mb-3 ${showFullProfile ? '' : 'max-h-10 overflow-hidden'}`}>
                        <p className="text-xs font-semibold text-slate-gray uppercase tracking-wide mb-1.5">Hobbies</p>
                        <div className="flex flex-wrap gap-1.5">
                          {currentProfile.hobbies.map((hobby, i) => (
                            <Badge key={i} variant="secondary" className="bg-purple-50 text-purple-700 text-xs">{hobby}</Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {currentProfile.bio && (
                      <p className={`text-dark-slate text-sm mb-3 ${showFullProfile ? '' : 'line-clamp-2'}`}>
                        {currentProfile.bio}
                      </p>
                    )}

                    {currentProfile.interests?.length > 0 && (
                      <div className={`flex flex-wrap gap-2 mb-3 ${showFullProfile ? '' : 'max-h-10 overflow-hidden'}`}>
                        {currentProfile.interests.map((interest, i) => (
                          <Badge key={i} variant="secondary" className="bg-rose-50 text-rose-700 text-xs">{interest}</Badge>
                        ))}
                      </div>
                    )}

                    <button onClick={() => setShowFullProfile(!showFullProfile)}
                      className="text-xs text-slate-gray flex items-center mx-auto">
                      {showFullProfile ? <><ChevronUp className="w-3 h-3 mr-1" />Less</> : <><ChevronDown className="w-3 h-3 mr-1" />More</>}
                    </button>
                  </div>

                  {/* Action buttons */}
                  <div className="flex items-center justify-center space-x-4 p-5 pt-0">
                    <button onClick={handlePass}
                      className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center hover:bg-slate-200 transition-colors shadow-md active:scale-95">
                      <X className="w-8 h-8 text-slate-500" />
                    </button>
                    <button onClick={handleLike}
                      className="w-20 h-20 rounded-full bg-gradient-to-br from-rose-500 to-pink-500 flex items-center justify-center hover:from-rose-600 hover:to-pink-600 transition-colors shadow-lg active:scale-95">
                      <Heart className="w-10 h-10 text-white" />
                    </button>
                  </div>
                </motion.div>
              </AnimatePresence>
            )}
          </>
        ) : (
          /* Matches Tab */
          <div>
            {matches.length === 0 ? (
              <div className="bg-white rounded-3xl p-12 text-center border border-slate-100 shadow-ambient">
                <Heart className="w-16 h-16 text-slate-200 mx-auto mb-4" />
                <h3 className="font-heading font-bold text-xl text-dark-slate mb-2">No matches yet</h3>
                <p className="text-slate-gray mb-4">Keep browsing to find your match!</p>
                <Button onClick={() => setActiveTab('browse')} className="bg-dark-slate text-white rounded-full">
                  Browse Profiles
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {matches.map(match => (
                  <div key={match.profile_id || match.user_id}
                    className="bg-white rounded-2xl p-4 border border-slate-100 shadow-ambient flex items-center space-x-4">
                    <Avatar className="w-16 h-16">
                      <AvatarImage src={match.photos?.[0] || match.profile_photo} />
                      <AvatarFallback className="bg-rose-100 text-rose-600 text-xl">{match.name?.charAt(0)}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-dark-slate">{match.name}</h3>
                      {match.institution && <p className="text-xs text-slate-gray">{match.institution}</p>}
                      {match.bio && <p className="text-xs text-slate-gray line-clamp-1 mt-0.5">{match.bio}</p>}
                    </div>
                    <Button size="sm" onClick={() => navigate(`/chat/${match.user_id}`)}
                      className="bg-dark-slate text-white rounded-full">
                      <MessageCircle className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Dating;
